/**
 * Analytics initialization — Microsoft Clarity and Hotjar.
 * Environment is detected at runtime from the hostname so that a single
 * build artifact can serve dev / uat / prod with the correct tracking keys.
 */

interface AnalyticsConfig {
  clarityId: string;
  hotjarId: number;
  hotjarSv: number;
}

const CONFIGS: Record<string, AnalyticsConfig> = {
  dev: {clarityId: 'wgpaol3q72', hotjarId: 6705787, hotjarSv: 6},
  uat: {clarityId: 'wgpb67i1ox', hotjarId: 6705788, hotjarSv: 6},
  prod: {clarityId: 'wgpbm1w6ro', hotjarId: 6705790, hotjarSv: 6},
};

function detectEnvironment(): string | null {
  const hostname = window.location.hostname;
  if (hostname.includes('hemrepository-dev')) return 'dev';
  if (hostname.includes('hemrepository-uat')) return 'uat';
  if (hostname === 'hemrepository.com') return 'prod';
  return null;
}

function initClarity(id: string): void {
  (function (c: any, l: Document, a: string, r: string, i: string) {
    c[a] =
      c[a] ||
      function (...args: any[]) {
        (c[a].q = c[a].q || []).push(args);
      };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = 'https://www.clarity.ms/tag/' + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode!.insertBefore(t, y);
  })(window, document, 'clarity', 'script', id);

  // Mask all text and attribute values by default (strict mode).
  // Individual safe elements can opt out via the "clarity-unmask" CSS class.
  (window as any).clarity('set', 'masking', 'Strict');
}

function initHotjar(hjid: number, hjsv: number): void {
  (function (h: any, o: Document, t: string, j: string) {
    h.hj =
      h.hj ||
      function (...args: any[]) {
        (h.hj.q = h.hj.q || []).push(args);
      };
    h._hjSettings = {hjid, hjsv};
    const a = o.getElementsByTagName('head')[0];
    const r = o.createElement('script') as HTMLScriptElement;
    r.async = true;
    r.src = t + hjid + j + hjsv;
    a.appendChild(r);
  })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');

  // Suppress recording of all text content and attribute values inside the
  // app root. Hotjar heatmaps (clicks / scroll) remain fully functional.
  const root = document.getElementById('root');
  if (root) {
    root.setAttribute('data-hj-suppress', '');
  }
}

export function initAnalytics(): void {
  const env = detectEnvironment();
  if (!env) return;

  const config = CONFIGS[env];
  initClarity(config.clarityId);
  initHotjar(config.hotjarId, config.hotjarSv);
}
